import type { CampaignSpreadsheetViewType } from '../context/CampaignContext';
import { HubSpotTargetObjectName } from '../types/hubspot';
import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';
import { BASE_URL } from './constants';

/** Appends view_type query param to URL when provided. */
function withViewTypeQueryParam(
  url: string,
  viewType?: CampaignSpreadsheetViewType
): string {
  if (!viewType) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}view_type=${encodeURIComponent(viewType)}`;
}

/** Salesforce only: lookup field resolution prefers Name vs Id matching. */
export type SalesforceLookupSearchByPreference = 'Name' | 'Id';

/** CRM mapping as returned by the API. */
export interface CRMMappingResponse {
  campaign_id: string;
  target_object: string;
  field_mappings: Record<string, string>;
  /** Campaign column names used for deduplication lookups */
  dedup_fields: string[];
  /** Campaign column names to overwrite when duplicate is found */
  overwrite_on_dedup: string[];
  /** Keys are campaign column names; values match Salesforce LookupSearchBy. */
  salesforce_lookup_search_by?: Record<string, SalesforceLookupSearchByPreference>;
  create_ts?: string;
  update_ts?: string;
}

/** Payload for creating or replacing a CRM mapping (PUT). */
export interface CRMMappingCreate {
  target_object: string;
  field_mappings: Record<string, string>;
  dedup_fields: string[];
  overwrite_on_dedup: string[];
  salesforce_lookup_search_by?: Record<string, SalesforceLookupSearchByPreference>;
}

/** Payload for partial update (PATCH). */
export interface CRMMappingUpdate {
  target_object?: string;
  field_mappings?: Record<string, string>;
  dedup_fields?: string[];
  overwrite_on_dedup?: string[];
  salesforce_lookup_search_by?: Record<string, SalesforceLookupSearchByPreference>;
}

const CRM_MAPPING_BASE = `${BASE_URL}/v1/campaigns`;

function isSalesforceCrmMappingTarget(targetObject: string): boolean {
  return (
    targetObject !== HubSpotTargetObjectName.COMPANY &&
    targetObject !== HubSpotTargetObjectName.CONTACT
  );
}

export async function getCrmMapping(
  campaignId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<{ crm_mapping: CRMMappingResponse | null }> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${CRM_MAPPING_BASE}/${campaignId}/crm-mapping`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/**
 * When a spreadsheet column is renamed, CRM mapping keys (column names) must follow or the mapping
 * appears cleared. Call after the column update succeeds.
 */
export async function renameColumnInCrmMappingIfPresent(
  campaignId: string,
  viewType: CampaignSpreadsheetViewType | undefined,
  oldColumnName: string,
  newColumnName: string
): Promise<void> {
  if (oldColumnName === newColumnName) return;

  let crm_mapping: CRMMappingResponse | null;
  try {
    const res = await getCrmMapping(campaignId, viewType);
    crm_mapping = res.crm_mapping;
  } catch {
    return;
  }

  if (!crm_mapping?.target_object) return;

  const fieldMappings = { ...(crm_mapping.field_mappings ?? {}) };
  const hadFieldKey = Object.prototype.hasOwnProperty.call(
    fieldMappings,
    oldColumnName
  );
  const salesforce_lookup_search_by = {
    ...(crm_mapping.salesforce_lookup_search_by ?? {})
  };
  const hadLookupPref = Object.prototype.hasOwnProperty.call(
    salesforce_lookup_search_by,
    oldColumnName
  );

  const inDedup = crm_mapping.dedup_fields?.includes(oldColumnName) ?? false;
  const inOverwrite =
    crm_mapping.overwrite_on_dedup?.includes(oldColumnName) ?? false;

  if (!hadFieldKey && !inDedup && !inOverwrite && !hadLookupPref) return;

  if (hadFieldKey) {
    const crmField = fieldMappings[oldColumnName];
    delete fieldMappings[oldColumnName];
    fieldMappings[newColumnName] = crmField;
  }

  if (hadLookupPref) {
    const pref = salesforce_lookup_search_by[oldColumnName];
    delete salesforce_lookup_search_by[oldColumnName];
    salesforce_lookup_search_by[newColumnName] = pref;
  }

  const dedup_fields = (crm_mapping.dedup_fields ?? []).map((f) =>
    f === oldColumnName ? newColumnName : f
  );
  const overwrite_on_dedup = (crm_mapping.overwrite_on_dedup ?? []).map((f) =>
    f === oldColumnName ? newColumnName : f
  );

  await upsertCrmMapping(
    campaignId,
    {
      target_object: crm_mapping.target_object,
      field_mappings: fieldMappings,
      dedup_fields,
      overwrite_on_dedup,
      ...(isSalesforceCrmMappingTarget(crm_mapping.target_object)
        ? { salesforce_lookup_search_by }
        : {})
    },
    viewType
  );
}

export async function upsertCrmMapping(
  campaignId: string,
  data: CRMMappingCreate,
  viewType?: CampaignSpreadsheetViewType
): Promise<{ crm_mapping: CRMMappingResponse }> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${CRM_MAPPING_BASE}/${campaignId}/crm-mapping`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}
